import React, { useEffect, createElement } from 'react';
interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  canonicalPath?: string;
  language?: 'ar' | 'en';
  noIndex?: boolean;
  structuredData?: object;
}
const SITE_URL = 'https://operines.ai';
const DEFAULT_OG_IMAGE = 'https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png';
function setMeta(name: string, content: string, property = false) {
  const attr = property ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.querySelector(selector) as HTMLLinkElement;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
function setStructuredData(id: string, data: object) {
  let el = document.querySelector(`script[data-schema="${id}"]`) as HTMLScriptElement;
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-schema', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
export function SEO({
  title,
  description,
  keywords,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  canonicalPath = '',
  language = 'ar',
  noIndex = false,
  structuredData
}: SEOProps) {
  useEffect(() => {
    const isAr = language === 'ar';
    const fullTitle = title ? `${title} | Operines` : isAr ? 'Operines | منصة أتمتة واتساب بالذكاء الاصطناعي' : 'Operines | AI-Powered WhatsApp Automation Platform';
    const fullDesc = description || (isAr ? 'Operines منصة أتمتة واتساب بالذكاء الاصطناعي. اربط رقمك وابدأ الرد التلقائي على عملائك فوراً. تجربة مجانية 3 أيام.' : 'Operines is an AI-powered WhatsApp automation platform. Connect your number and start auto-replying to customers instantly. 3-day free trial.');
    const fullKeywords = keywords || (isAr ? 'أتمتة واتساب, ذكاء اصطناعي, بوت واتساب, ردود تلقائية, خدمة عملاء, مصر, الإمارات, السعودية, Operines' : 'WhatsApp automation, AI chatbot, WhatsApp bot, auto reply, customer service, Egypt, UAE, Saudi Arabia, Operines');
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    // ── Title ──────────────────────────────────────────────────────────────────
    document.title = fullTitle;
    // ── HTML lang ─────────────────────────────────────────────────────────────
    document.documentElement.lang = isAr ? 'ar' : 'en';
    // ── Basic Meta ────────────────────────────────────────────────────────────
    setMeta('description', fullDesc);
    setMeta('keywords', fullKeywords);
    setMeta('author', 'Operines');
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    setMeta('theme-color', '#4f1964');
    // ── Open Graph ────────────────────────────────────────────────────────────
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', fullDesc, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('og:site_name', 'Operines', true);
    setMeta('og:locale', isAr ? 'ar_EG' : 'en_US', true);
    setMeta('og:locale:alternate', isAr ? 'en_US' : 'ar_EG', true);
    // ── Twitter Card ──────────────────────────────────────────────────────────
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', fullDesc);
    setMeta('twitter:image', ogImage);
    setMeta('twitter:site', '@operines_ai');
    // ── Canonical & hreflang ──────────────────────────────────────────────────
    setLink('canonical', canonicalUrl);
    setLink('alternate', `${SITE_URL}${canonicalPath}`, 'ar');
    setLink('alternate', `${SITE_URL}${canonicalPath}`, 'en');
    setLink('alternate', `${SITE_URL}${canonicalPath}`, 'x-default');
    // ── Organization JSON-LD ──────────────────────────────────────────────────
    setStructuredData('organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Operines',
      url: SITE_URL,
      logo: DEFAULT_OG_IMAGE,
      description: isAr ? 'منصة أتمتة واتساب بالذكاء الاصطناعي' : 'AI-powered WhatsApp automation platform',
      email: 'hello@operines.ai',
      telephone: '+201508087501',
      sameAs: ['https://www.linkedin.com/company/operines', 'https://www.instagram.com/operines', 'https://www.facebook.com/operines'],
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'EG'
      }
    });
    // ── WebSite JSON-LD (enables Google Sitelinks search box) ─────────────────
    setStructuredData('website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Operines',
      url: SITE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    });
    // ── Custom structured data ────────────────────────────────────────────────
    if (structuredData) {
      setStructuredData('custom', structuredData);
    }
  }, [title, description, keywords, ogImage, ogType, canonicalPath, language, noIndex, structuredData]);
  return null;
}